export const tsmtPitch = {
  hero: {
    title: "How Can Bespire Add a New Digital Growth Layer to the TSMT® Model?",
    subtitle: "Executive decision-support overview of a modern, scalable training, content monetization, and professional referral model",
    badges: {
      info: "Information Hub",
      training: "Training Sales",
      digital: "Digital Content",
      network: "Professional Referral",
    },
    mockupCards: {
      website: "Modern Website",
      booking: "Booking System",
      courses: "Online Courses",
      profiles: "Professional Profiles",
    },
  },
  executiveSummary: {
    title: "In Brief: Why Is This Relevant for TSMT®?",
    subtitle: "The most important strategic findings at a glance",
    points: [
      {
        title: "Strong Professional Brand",
        description: "TSMT® is already a strong professional brand with a structured knowledge system — this is the foundation worth building upon digitally.",
      },
      {
        title: "Next Growth Level",
        description: "The next growth level lies in developing the digital experience, sales processes, and network operations.",
      },
      {
        title: "Unified System",
        description: "Bespire can manage content, training, digital products, and professional referral logic in a single system.",
      },
      {
        title: "Triple Improvement",
        description: "User experience, administrative efficiency, and revenue-generating capability can all improve simultaneously.",
      },
      {
        title: "Gradual Implementation",
        description: "All of this can be introduced gradually, with low risk, without disrupting existing operations.",
      },
    ],
  },
  strengths: {
    title: "Strong Professional Foundations, Strong Brand, Real Nationwide Presence",
    subtitle: "TSMT® Model's outstanding professional values worth building upon digitally",
    items: [
      {
        title: "Methodological Background",
        description: "TSMT® has a unique, scientifically-based methodological framework recognized both domestically and internationally.",
      },
      {
        title: "Dual Target Audience",
        description: "The model simultaneously addresses parents seeking solutions for their children and professionals looking to master the methodology.",
      },
      {
        title: "Structured Training System",
        description: "A multi-level training and continuing education system, further strengthened by the TVTH quality assurance logic since July 2024.",
      },
      {
        title: "Nationwide Professional Network",
        description: "Graduated therapists are available nationwide, providing real coverage for parents across the country.",
      },
      {
        title: "Quality Assurance Logic",
        description: "TVTH members renew their knowledge every 5 years, ensuring continuous professional standards and parental trust.",
      },
    ],
  },
  growthOpportunity: {
    title: "The Next Level Is Not Just Information, But Digital Operations",
    subtitle: "Typical growth stages for a training organization — and where digital operations truly create value",
    funnel: [
      {
        from: "From information",
        to: "to conversion",
        description: "Visitors should not just read but take action: apply, inquire, purchase.",
      },
      {
        from: "From prospect",
        to: "to applicant",
        description: "A simple, clear application pathway that doesn't require manual coordination.",
      },
      {
        from: "From applicant",
        to: "to paying participant",
        description: "With integrated payment, interest can immediately become a purchase.",
      },
      {
        from: "From participant",
        to: "to returning customer",
        description: "Through continuing education, digital materials, and exclusive content, the relationship doesn't end with training.",
      },
      {
        from: "From graduated professional",
        to: "to active network member",
        description: "A graduated therapist is not just a name in the database but a visible, accessible, recommendable professional.",
      },
      {
        from: "From professional content",
        to: "to recurring digital revenue",
        description: "Once-created materials can generate value again and again — as digital products, courses, or exclusive content.",
      },
    ],
  },
  challenge: {
    title: "Knowledge, Training, Administration, and Network Operations Often Live in Separate Systems",
    subtitle: "Natural challenges of digitally scaling a strong professional model",
    items: [
      {
        title: "Fragmented Digital Experience",
        description: "Content, applications, payments, and professional search are available on different platforms with varying experiences.",
      },
      {
        title: "Manual Administration",
        description: "Processing applications, sending confirmations, and reminders is often a manual or semi-automated process.",
      },
      {
        title: "Complex Application Path",
        description: "The journey from prospect to paying participant may involve multiple steps, separate systems, and coordination.",
      },
      {
        title: "Missing Automations",
        description: "Automating reminders, confirmations, and follow-ups would free up resources and time.",
      },
      {
        title: "Digital Content Monetization",
        description: "A significant portion of existing professional knowledge is only available as information, not as structured digital products.",
      },
      {
        title: "Professional Network Visibility",
        description: "The accessibility and referability of graduated professionals may be limited within current digital frameworks.",
      },
    ],
  },
  bespireRole: {
    title: "Bespire as a Unified Digital Operations Layer",
    subtitle: "Bespire is not just a website builder — it's a unified platform capable of coordinating the entire digital operations of the organization",
    capabilities: [
      { title: "Public Information Layer", description: "Modern, mobile-friendly website with all organizational content" },
      { title: "Sales Processes", description: "Training, digital products, and services sold in one place" },
      { title: "Booking & Application Logic", description: "Integrated application system with automatic processing" },
      { title: "Online Payment", description: "Secure payment solution built into the complete purchase flow" },
      { title: "Automatic Reminders", description: "Email and notification automations throughout the user journey" },
      { title: "User Administration", description: "Managing participants, applicants, and members on a single interface" },
      { title: "Digital Content Delivery", description: "Selling downloadable materials, videos, and online courses" },
      { title: "Professional Referral", description: "Profiles, searchability, and referral logic for the professional network" },
      { title: "Parent Admin Panel", description: "Parents can track their child's progress, bookings, and history on a dedicated interface" },
      { title: "Therapist Admin Panel", description: "Therapists can manage their profile, schedule, and clients on a dedicated dashboard" },
      { title: "Self-Service Development (No-Code)", description: "The organization can shape and expand its digital presence without coding skills — faster turnaround, lower costs, full control" },
      { title: "Marketing Automation", description: "AI-driven campaign management: social media posts, email sequences, customer re-engagement, and targeted communications automatically from a chat interface" },
    ],
  },
  pillar1: {
    title: "Existing Professional Content Is Not Lost — It Operates in a More Modern Digital Format",
    subtitle: "Pillar 1: Original Content Migration and Information Delivery",
    blocks: [
      {
        title: "Content Reorganization",
        description: "Current valuable professional and educational content can be migrated to a more modern, easily navigable structure — without losing anything.",
      },
      {
        title: "Target Group-Based Entry Points",
        description: "Parents, prospective therapists, and interested professionals can each access content through relevant entry points tailored to them.",
      },
      {
        title: "Stronger Conversion Pathways",
        description: "Informational content can be more easily directed toward concrete action: application, contact, purchase.",
      },
      {
        title: "More Modern Digital Experience",
        description: "A more unified, faster, mobile-friendly interface that presents the TSMT® brand in a professional and contemporary format.",
      },
    ],
  },
  pillar2: {
    title: "Training Can Be Not Only Communicated, But Fully Sold and Managed",
    subtitle: "Pillar 2: Complete Training Sales",
    participant: {
      title: "From the Participant's Side",
      items: [
        "Easy-to-navigate training pages with detailed information",
        "Simple, few-click application process",
        "Immediate online payment option",
        "Automatic confirmation and reminders",
        "Personal user account for tracking training history",
      ],
    },
    organizer: {
      title: "From the Organizer's Side",
      items: [
        "Parallel management of multiple trainings and target groups",
        "Automatic application processing and confirmation",
        "Structured collection of required data and information",
        "Automatic reminder delivery before training",
        "Significantly reduced administrative burden and more transparent operations",
      ],
    },
  },
  pillar3: {
    title: "Recurring Digital Revenue Streams Can Be Built from Professional Knowledge",
    subtitle: "Pillar 3: Digital Content Sales",
    intro: "The same knowledge can create value not only as information but as structured digital products — as a recurring revenue source.",
    products: [
      { title: "Downloadable Professional Materials", description: "Methodological summaries, practical guides in PDF or e-book format" },
      { title: "Parent Support Materials", description: "Guides for home exercises and developmental activities" },
      { title: "Video and Modular Content", description: "Short educational videos, training modules in online format" },
      { title: "Knowledge Refresh Content", description: "Materials supporting the 5-year knowledge renewal requirement of the TVTH system" },
      { title: "Exclusive Professional Content", description: "Methodological documents accessible only to registered professionals" },
      { title: "Preparatory and Follow-up Materials", description: "Pre-training and post-training course materials for participants" },
    ],
  },
  pillar4: {
    title: "The Professional Network Can Be More Than a Database — It Can Be an Active Digital Service Layer",
    subtitle: "Pillar 4: Professional Referral and Visibility",
    features: [
      { title: "Therapist and Professional Profiles", description: "Detailed, trustworthy profile pages for every graduated professional" },
      { title: "Geographic and Specialty Search", description: "Parents can search for professionals by location, area, or specialty" },
      { title: "Trust-Building Display", description: "Qualifications, experience, and quality assurance status shown on profiles" },
      { title: "Contact and Referral Pathways", description: "Simple contact, application, or appointment request directly from profiles" },
      { title: "Stronger Network Visibility", description: "A unified, professional digital presence for the professional network" },
      { title: "Connecting Parent Needs with Provider Offerings", description: "Better matching between what parents seek and what professionals offer" },
    ],
  },
  businessValue: {
    title: "The Value Is Not Only Technological, But Business and Operational Too",
    subtitle: "Implementing Bespire can deliver measurable benefits across three dimensions",
    columns: {
      revenue: {
        title: "Revenue Benefits",
        items: [
          "More paying applicants for trainings",
          "Better conversion from interest to purchase",
          "New, recurring digital revenue stream",
          "Wider reach through online sales",
        ],
      },
      operations: {
        title: "Operational Benefits",
        items: [
          "Reduced administrative burden",
          "Automated application and payment processes",
          "More unified, transparent operations",
          "Less manual coordination and follow-up",
        ],
      },
      brand: {
        title: "Brand & Network Benefits",
        items: [
          "More modern, professional brand experience",
          "Stronger professional network presence",
          "Better user journey for parents and professionals",
          "Strengthening TSMT®'s digital brand position",
        ],
      },
    },
  },
  roadmap: {
    title: "Quick to implement, immediate value",
    subtitle: "No need to wait months for results — the first phase can go live in as little as 2–4 weeks",
    phases: [
      {
        number: "1",
        title: "Information and Conversion Layer",
        timeline: "2–4 weeks",
        items: [
          "Restructuring existing professional content",
          "New, target group-specific landing pages",
          "Separating parent and professional entry points",
          "Building CTAs and conversion pathways",
        ],
      },
      {
        number: "2",
        title: "Training Sales and User Administration",
        timeline: "+4–6 weeks",
        items: [
          "Online application and payment process",
          "Automatic confirmations and reminders",
          "User accounts and training history",
          "Simplifying administrative processes",
        ],
      },
      {
        number: "3",
        title: "Digital Content and Professional Network",
        timeline: "+6–8 weeks",
        items: [
          "Selling digital products and exclusive content",
          "Professional profiles and search logic",
          "Referral and recommendation system",
          "Complete network display",
        ],
      },
    ],
  },
  whyNow: {
    title: "Why Might Now Be the Right Time to Take This Step?",
    subtitle: "Digitalization doesn't start from zero — it can be the next development stage of an existing, credible system",
    points: [
      {
        title: "The Professional Foundation Already Exists",
        description: "TSMT®'s methodological background, training system, and quality assurance logic is mature and structured.",
      },
      {
        title: "The Brand Already Exists",
        description: "TSMT® is a nationally recognized and respected brand — digital presence is the online extension of this strength.",
      },
      {
        title: "The Network Already Exists",
        description: "Graduated therapists, active professionals, and parent community — the network is alive, only the digital layer is missing.",
      },
      {
        title: "The Demand Side Already Exists",
        description: "Parents and prospective therapists actively seek TSMT® — the question is what experience they get upon arrival.",
      },
      {
        title: "The Next Step Is Logical",
        description: "This is not about a risky switch, but the natural digital evolution of an existing, functioning system.",
      },
    ],
  },
  closing: {
    title: "The Question Is Not Whether TSMT® Has Value — But How to Better Unlock It Digitally",
    message: "With Bespire, TSMT® can move toward a more modern digital operating model that:",
    benefits: [
      "Preserves professional credibility",
      "Improves user experience",
      "Simplifies administration",
      "Opens new revenue opportunities",
      "Strengthens professional network visibility",
    ],
    cta: {
      label: "Recommended Next Step",
      description: "Joint discovery workshop to determine priorities and the best implementation model",
      button: "Schedule Workshop",
    },
  },
  therapistValue: {
    title: "What does a therapist gain if TSMT® uses the Bespire system?",
    subtitle: "Digital development benefits not only the organization but can also deliver tangible value for therapists working within the network",
    cards: [
      {
        title: "Visibility",
        items: [
          "Structured, unified professional profile",
          "Easier discoverability for parents and interested parties",
          "Trust-building presence in a professionally strong system",
          "Geographic or specialty-based searchability",
          "Clearer, more transparent self-presentation",
        ],
      },
      {
        title: "Inquiries",
        items: [
          "Parents can more easily find the right professional",
          "More structured incoming inquiries",
          "No more scattered communication channels",
          "More predictable and organized contact process",
          "Referral logic that connects needs with the right specialist",
        ],
      },
      {
        title: "Simpler Operations",
        items: [
          "Less manual coordination",
          "More transparent information management",
          "Automatic notifications and reminders",
          "Easier access to relevant professional information",
          "More organized digital environment for daily operations",
        ],
      },
      {
        title: "Professional Connection",
        items: [
          "Closer connection to the TSMT® professional ecosystem",
          "Easier access to trainings and knowledge resources",
          "Better trackable professional paths and opportunities",
          "Stronger community and network experience",
          "More modern, supportive digital foundation for professional presence",
        ],
      },
    ],
    summary: "From a therapist's perspective, the Bespire-supported TSMT® system is not just a new interface, but a digital foundation that can enhance visibility, connectivity, simplify administration, and enable more active participation in the professional network.",
  },
  demo: {
    label: "Live Demo",
    title: "The enhanced TSMT® website demo is already built",
    description: "We migrated and upgraded the existing tsmtmodell.hu content into a modern, feature-rich platform — one that doesn't just inform, but sells, connects, and automates.",
    features: [
      "Original content fully migrated — all information preserved",
      "Training sales with payments, reminders, and user admin",
      "Digital content sales",
      "Therapist directory and matching",
    ],
    cta: "View Demo Page",
    note: "The demo is an enhanced version of tsmtmodell.hu — this is what the live system could look like.",
  },
};
